// Brief 85 §4 — PartyDetailDialog, moved out of App.tsx.
//
// 213B §19 — REWRITTEN: the window shows only the fields it can NAME, each
// labelled in the reader's language, each value through the app's own reader.
// Before (the census, docs/213b-deploy-note.md §8.19) it listed every scalar
// field of the record minus a skip list, printed a field it had no label for by
// its code name (`labelMap[k] || k` — « nationalRegister », « language »,
// « bceNumber », « createdAt »…), and printed values raw: the type as the code
// with a capital (« Company »), a country as its code (« LU »), a language as
// « nl », a date as an ISO stamp, a yes/no as « true ».
//
// Now: one list per kind of party, in the order a card reads — who, how to
// reach them, where, their numbers, their papers, the notes — with the label's
// seeded key (`field.*` / `cdd.field.*`, none new) and a reader: the party
// type through `type.company` / `type.individual`, a country or a nationality
// through `fcountry` (the ISO 3166 taxonomy, as a document's address prints
// it), a language through `languageName` (Intl.DisplayNames), a date through
// `fd`, a yes/no through `common.yes` / `common.no`. Technical fields — ids,
// the folder path, archived, isProspect, the CRM pipeline, the stamps — are not
// shown (the census names each; what counts as technical is an [ADVISOR]
// line there).

import { FS } from "../../lib/tokens";
import { ModalLayout } from "../SharedUI";
import { I, fd, fcountry } from "../../utils";
import { Pencil } from "lucide-react";
import { pname } from "../../utils";
import { languageName } from "../../lib/docLang.mjs";
import type { TFn } from "../../lib/taxonomyTypes";

type Props = {
  clients: readonly unknown[];
  openModal: (t: unknown, e?: unknown) => void;
  opponents: readonly unknown[];
  partyDetailDlg: { type?: string | number | null; item?: { clientId?: string | number | null; opponentId?: string | number | null } };
  setPartyDetailDlg: (v: unknown) => void;
  t: TFn;
  /** The reader's language (`i18n.lang`), for a language's name. */
  lang?: string;
};

/** How a field is read: a plain string, a party type, a country (or nationality), a language, a date, a yes/no. */
type Reader = "text" | "partyType" | "country" | "language" | "date" | "yesNo";
/** A field the window can name: its column, its seeded label key with the English fallback, its reader. */
type Field = readonly [key: string, labelKey: string, fallback: string, reader?: Reader];

const WHO: readonly Field[] = [
  ["title", "field.title", "Title"],
  ["firstName", "field.first_name", "First Name"],
  ["lastName", "field.last_name", "Last Name"],
  ["companyName", "field.company_name", "Company Name"],
  ["type", "field.type", "Type", "partyType"],
  ["function", "field.function_role", "Function / Role"],
  ["company", "field.company", "Company"],
  ["firm", "field.firm", "Firm"],
  ["office", "field.office", "Office"],
  ["spec", "field.specialty", "Specialty"],
  ["specialty", "field.specialty", "Specialty"],
];
const REACH: readonly Field[] = [
  ["email", "field.email", "Email"],
  ["phone", "field.phone", "Phone"],
  ["contact", "field.contact", "Contact"],
  ["language", "field.language", "Language", "language"],
];
const WHERE: readonly Field[] = [
  ["street", "field.street", "Street"],
  ["postalCode", "field.postal_code", "Postal Code"],
  ["city", "field.city", "City"],
  ["country", "field.country", "Country", "country"],
];
const NUMBERS: readonly Field[] = [
  ["vat", "field.vat", "VAT"],
  ["bceNumber", "cdd.field.bce", "Enterprise number"],
  ["nationalRegister", "field.national_register_number", "National Register Number"],
  ["contractNumber", "field.contractNumber", "Contract Number"],
  ["formerCaseRef", "field.former_case_ref", "Former case ref"],
  ["quantum", "field.quantum", "Quantum"],
  ["bar", "field.bar", "Bar"],
  ["barAssociation", "field.bar_association", "Bar Association"],
  ["professionalRegistration", "field.professional_registration", "Professional Registration"],
];
const PAPERS: readonly Field[] = [
  ["dateOfBirth", "cdd.field.dob", "Date of birth", "date"],
  ["placeOfBirth", "cdd.field.pob", "Place of birth"],
  ["nationality", "cdd.field.nationality", "Nationality", "country"],
  ["idDocumentType", "cdd.field.id_doc_type", "ID document type"],
  ["idDocumentNumber", "cdd.field.id_doc_number", "ID document number"],
  ["idDocumentIssuedAt", "cdd.field.id_doc_issued_at", "ID issued", "date"],
  ["idDocumentExpiresAt", "cdd.field.id_doc_expires_at", "ID expires", "date"],
];
const NOTES: readonly Field[] = [["notes", "field.notes", "Notes"]];

/** The fields each kind of party can bring (the Prisma models; the census lists them): a card's order. */
const FIELDS: readonly Field[] = [...WHO, ...REACH, ...WHERE, ...NUMBERS, ...PAPERS, ...NOTES];

/** A stored scalar read as a string, or null when there is nothing to show. */
const scalarOf = (v: unknown): string | null => {
  if (v == null || v === "" || typeof v === "object") return null;
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v as string | number);
};

/** The value through its reader, in the reader's language. */
function readValue(t: TFn, reader: Reader | undefined, raw: string, lang: string): string {
  switch (reader) {
    case "partyType":
      if (raw === "company") return t("type.company", { fallback: "Company" });
      if (raw === "individual") return t("type.individual", { fallback: "Individual" });
      return raw;
    case "country":
      return String(fcountry(raw) ?? raw);
    case "language":
      return languageName(raw, lang);
    case "date":
      return fd(raw);
    case "yesNo":
      return raw === "yes" ? t("common.yes", { fallback: "Yes" }) : t("common.no", { fallback: "No" });
    default:
      return raw;
  }
}

export default function PartyDetailDialog({
  clients,
  openModal,
  opponents,
  partyDetailDlg,
  setPartyDetailDlg,
  t,
  lang = "fr",
}: Props) {
    if (!partyDetailDlg) return null;
    const { type, item } = partyDetailDlg;
    if (!item) return null;
    const record = item as Record<string, unknown>;
    const rows = FIELDS.flatMap(([k, labelKey, fallback, reader]) => {
      const raw = scalarOf(record[k]);
      return raw == null ? [] : [{ k, label: t(labelKey, { fallback }), value: readValue(t, reader, raw, lang) }];
    });
    let linkInfo = null;
    if (type === "cp" && item.clientId) {
      const parent = clients.find((c: any) => c.id === item.clientId);
      if (parent)
        linkInfo = {
          label: t("list.section.linked_client", { fallback: "Linked Client" }),
          value: pname(parent),
        };
    }
    if (type === "oc" && item.opponentId) {
      const parent = opponents.find((o: any) => o.id === item.opponentId);
      if (parent)
        linkInfo = {
          label: t("list.section.linked_opponent", { fallback: "Linked Opponent" }),
          value: pname(parent),
        };
    }
    const closeAndEdit = () => {
      const it = item;
      setPartyDetailDlg(null);
      setTimeout(() => openModal(type, it), 0);
    };
    return (
      <ModalLayout
        title={pname(item)}
        onClose={() => setPartyDetailDlg(null)}
        closeOnOverlay
        maxWidth={480}
        rawBody
        footer={null}
      >
          <div
            style={{
              padding: "16px 20px",
              maxHeight: "60vh",
              overflowY: "auto",
            }}
            data-party-detail={String(type ?? "")}
          >
            {rows.length === 0 && !linkInfo ? (
              <p
                style={{
                  fontSize: FS.s11,
                  color: "var(--tm)",
                  fontStyle: "italic",
                }}
              >
                {t("modal.party.no_details", {
                  fallback: "No additional details recorded.",
                })}
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gap: "6px 14px",
                  fontSize: FS.s11_5,
                }}
              >
                {rows.map(({ k, label, value }) => (
                  <div key={k} style={{ display: "contents" }} data-party-field={k}>
                    <span style={{ color: "var(--tm)", fontWeight: 500 }}>
                      {label}
                    </span>
                    <span
                      style={{
                        color: "var(--tp)",
                        wordBreak: "break-word",
                        whiteSpace: k === "notes" ? "pre-wrap" : "normal",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
                {linkInfo && (
                  <div style={{ display: "contents" }}>
                    <span style={{ color: "var(--tm)", fontWeight: 500 }}>
                      {linkInfo.label}
                    </span>
                    <span style={{ color: "var(--ac)", fontWeight: 500 }}>
                      {linkInfo.value}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 18,
                paddingTop: 12,
                borderTop: "1px solid var(--br)",
              }}
            >
              <button
                className="btn btn-ghost"
                style={{ padding: "6px 14px" }}
                onClick={() => setPartyDetailDlg(null)}
              >
                {t("common.close", { fallback: "Close" })}
              </button>
              <button
                className="btn btn-primary"
                style={{ padding: "6px 14px" }}
                onClick={closeAndEdit}
              >
                <Pencil size={I.s} style={{ marginRight: 4 }} />
                {t("common.edit_short", { fallback: "Edit" })}
              </button>
            </div>
          </div>
      </ModalLayout>
    );
}
